import React from 'react';
import { ReviewDefaultsPermissions } from '../../../api/structs/pipeline';
import {
  CapabilitySwitch,
  InheritCaption,
  InheritedStatus,
  fieldCaption,
  fieldLabel,
} from './reviewFormStyles';

/** The cap's range, mirroring what the engine accepts. */
export const CROSS_SCENE_MIN_PER_RUN = 0;
export const CROSS_SCENE_MAX_PER_RUN = 10;

/**
 * The cap, as it is allowed to be STORED. `min`/`max` on a number input are
 * advisory — typing 99 or -5 into one is perfectly legal — so the value is
 * clamped where it is written rather than only where the account saves it, or
 * a pipeline's block would carry a number the engine never accepts.
 */
export const clampCrossSceneMax = (value: number): number =>
  Math.min(CROSS_SCENE_MAX_PER_RUN, Math.max(CROSS_SCENE_MIN_PER_RUN, Math.round(value)));

/** What the layers ABOVE resolve the permission fields to. */
export interface ResolvedReviewPermissions {
  cross_scene_regenerate: boolean;
  cross_scene_max_per_run: number;
}

export interface ReviewPermissionsFieldsProps {
  /** What THIS layer declares. An absent (or null) key means inherit. */
  value: ReviewDefaultsPermissions;
  inherited: ResolvedReviewPermissions;
  /**
   * What the layer above is called, for the captions. Omitted at the ACCOUNT
   * layer, which has nothing above it: every field there is always set, so an
   * "inherits: …" caption would be describing a state it cannot reach.
   */
  inheritsFrom?: string;
  /** Whether `inherited` is actually known yet — see `InheritCaption`. */
  inheritedStatus?: InheritedStatus;
  /** Per-instance prefix for the DOM ids; two layers can be mounted at once. */
  idPrefix: string;
  /** The complete next block, with unset fields DELETED rather than nulled. */
  onChange: (next: ReviewDefaultsPermissions) => void;
}

/**
 * The reviewer's cross-scene permission — the one thing it may do to a scene
 * the operator already accepted — for the two layers that declare it: the
 * account's Reviewer settings and a pipeline's `review_defaults`.
 *
 * One component because the two say the same thing about the same keys; what
 * differs is only whether "inherit" is expressible, which is what `inheritsFrom`
 * decides. The suggestion itself is never gated by this: with the permission
 * off the verdict panel shows the same fix as a button instead.
 */
const ReviewPermissionsFields: React.FC<ReviewPermissionsFieldsProps> = ({
  value,
  inherited,
  inheritsFrom,
  inheritedStatus,
  idPrefix,
  onChange,
}) => {
  const canInherit = inheritsFrom !== undefined;
  const regenerateSet = typeof value.cross_scene_regenerate === 'boolean';
  const maxSet =
    typeof value.cross_scene_max_per_run === 'number' &&
    Number.isFinite(value.cross_scene_max_per_run);

  const regenerate = regenerateSet
    ? Boolean(value.cross_scene_regenerate)
    : inherited.cross_scene_regenerate;
  const maxPerRun = maxSet
    ? (value.cross_scene_max_per_run as number)
    : inherited.cross_scene_max_per_run;

  const set = <K extends keyof ReviewDefaultsPermissions>(
    key: K,
    next: ReviewDefaultsPermissions[K]
  ) => onChange({ ...value, [key]: next });

  const unset = (key: keyof ReviewDefaultsPermissions) => {
    const next = { ...value };
    delete next[key];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div>
        <CapabilitySwitch
          field="cross_scene_regenerate"
          label="Fix earlier scenes"
          checked={regenerate}
          onToggle={() => set('cross_scene_regenerate', !regenerate)}
        />
        {canInherit && (
          <InheritCaption
            field="cross_scene_regenerate"
            from={inheritsFrom as string}
            status={inheritedStatus}
            inherits={inherited.cross_scene_regenerate ? 'on' : 'off'}
            isSet={regenerateSet}
            onReset={() => unset('cross_scene_regenerate')}
          />
        )}
        <p className={fieldCaption}>
          The reviewer may regenerate an earlier scene that contradicts the current frame.
        </p>
      </div>

      <div className="w-48">
        <label className={fieldLabel} htmlFor={`${idPrefix}-cross-scene-max-per-run`}>
          Max earlier-scene fixes per run
        </label>
        <input
          id={`${idPrefix}-cross-scene-max-per-run`}
          data-testid="review-cross-scene-max-per-run"
          type="number"
          min={CROSS_SCENE_MIN_PER_RUN}
          max={CROSS_SCENE_MAX_PER_RUN}
          step={1}
          inputMode="numeric"
          className="input w-full"
          // Empty is how "inherit" reads on a number field; the account layer
          // has no such state, so it always shows its own value.
          value={canInherit && !maxSet ? '' : maxPerRun}
          placeholder={canInherit ? String(inherited.cross_scene_max_per_run) : undefined}
          onChange={(event) => {
            const raw = event.target.value;
            if (canInherit && raw === '') {
              unset('cross_scene_max_per_run');
              return;
            }
            const parsed = Number(raw);
            // Half-typed junk ("-", "e") is not a value; leave the field as it
            // was rather than writing a NaN clamped to the floor.
            if (!Number.isFinite(parsed)) return;
            set('cross_scene_max_per_run', clampCrossSceneMax(parsed));
          }}
        />
        {canInherit && (
          <InheritCaption
            field="cross_scene_max_per_run"
            from={inheritsFrom as string}
            status={inheritedStatus}
            inherits={String(inherited.cross_scene_max_per_run)}
            isSet={maxSet}
            onReset={() => unset('cross_scene_max_per_run')}
          />
        )}
        <p className={fieldCaption}>0 = the reviewer only ever suggests the fix.</p>
      </div>
    </div>
  );
};

export default ReviewPermissionsFields;
